import { LanguagesEnum } from '@/api/enums';
import { handleAxiosError } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account } from '@/client/accounts';
import { Course } from '@/client/courses';
import { User } from '@/client/users';
import { aiCustomizationLanguages } from '@/common/constants/languages';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants/routes';
import { TranslationFunctionType } from '@/common/types';
import { CourseTranslation } from '@/components/courses';
import {
  useCourseChangesPendingEdits,
  useSaveCourseChanges,
} from '@/components/courses/course-changes';
import { CourseProcessing } from '@/components/courses/CourseProcessing';
import {
  CustomizeCourseCompleteStep,
  CustomizeCoursePreferencesStep,
  CustomizeCoursePreviewStep,
  CustomizeCourseReviewStep,
  CustomizeCourseStepsIndicator,
  FeatureRoadmapSection,
  StepState,
} from '@/components/courses/customization';
import { useCourse } from '@/hooks/query';
import { useCourseChanges } from '@/hooks/query/course-changes.hooks';
import { useCustomizeCourse } from '@/hooks/query/course-customization.hooks';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { AppButton } from '@/ui';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { FlexContainer } from '@/ui/styled-ui';
import {
  determinePreselectedCourseLanguage,
  getTranslationByLanguage,
} from '@/utils/helpers';
import { AxiosError } from 'axios';
import { Divider } from 'primereact/divider';
import { MenuItem } from 'primereact/menuitem';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { STEP_CONFIG, STEP_ORDER } from './customization/constants';
import { CustomizationStep } from './customization/types';

const getCourseName = (
  user: User,
  course: Course,
  customizedCourse?: Course,
) => {
  if (customizedCourse) {
    return getTranslationByLanguage(
      customizedCourse?.courseTranslations,
      (user?.language as LanguagesEnum) || customizedCourse?.defaultLanguage,
      customizedCourse?.defaultLanguage,
    )?.name;
  }

  return getTranslationByLanguage(
    course?.courseTranslations,
    (user?.language as LanguagesEnum) || course.defaultLanguage,
    course.defaultLanguage,
  )?.name;
};

const getBreadcrumbs = (
  account: Account,
  course: Course,
  currentUser: User,
  t: TranslationFunctionType,
  customizedCourse?: Course,
): MenuItem[] => {
  return [
    {
      label: account?.name,
      url: !account?.isSystem
        ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
        : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
      template: AppBreadCrumbTemplate,
    },
    {
      label: t('content'),
      url: RedirectPaths[RedirectPathsEnum.COURSES](),
      template: AppBreadCrumbTemplate,
      data: { end: true },
    },
    {
      label: getCourseName(currentUser, course, customizedCourse),
      url: RedirectPaths[RedirectPathsEnum.COURSES_CONTENT](
        customizedCourse?.id || course.id,
      ),
      template: AppBreadCrumbTemplate,
    },
    {
      label: t('course.customization.button'),
      url: RedirectPaths[RedirectPathsEnum.COURSES_CUSTOMIZE](
        customizedCourse?.id || course.id,
      ),
      template: AppBreadCrumbTemplate,
    },
  ];
};

export const CustomizeCoursePage: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { id: courseId } = useParams<{ id: string }>();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);
  const [selectedLanguage, setSelectedLanguage] = useState<LanguagesEnum>();
  const [currentStep, setCurrentStep] = useState<CustomizationStep>(
    CustomizationStep.PREFERENCES,
  );
  const [customizedCourseId, setCustomizedCourseId] = useState<string | null>(
    null,
  );
  const {
    pendingSlideEdits,
    pendingQuestionEdits,
    handleSlideEdit,
    handleQuestionEdit,
    clearPendingEdits,
  } = useCourseChangesPendingEdits();

  // Fetch course data from URL params
  const { course, isLoading: isCourseLoading } = useCourse({
    courseId: courseId || undefined,
    enabled: !!courseId,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!course || selectedLanguage) {
      return;
    }

    const selectableLanguages = aiCustomizationLanguages.filter((language) =>
      course.courseLanguages.includes(language),
    );

    if (!selectableLanguages.length) {
      return;
    }

    const accountLanguage = currentUser?.language as LanguagesEnum | undefined;

    const preselectedLanguage =
      accountLanguage && selectableLanguages.includes(accountLanguage)
        ? accountLanguage
        : determinePreselectedCourseLanguage(currentUser, course);

    setSelectedLanguage(
      selectableLanguages.includes(preselectedLanguage)
        ? preselectedLanguage
        : selectableLanguages[0],
    );
  }, [course, currentUser, selectedLanguage]);

  const { mutate: customizeCourse, isLoading } = useCustomizeCourse({
    onSuccess: (data) => {
      setCustomizedCourseId(data.id);
      setCurrentStep(CustomizationStep.REVIEW);
    },
    onError: (error) => {
      handleAxiosError(error as AxiosError, toast);
    },
  });

  // Fetch course changes when in review step
  const {
    data: courseChanges,
    isLoading: isLoadingChanges,
    refetch: refetchCourseChanges,
  } = useCourseChanges(currentAccount?.id, customizedCourseId || undefined, {
    enabled:
      currentStep === CustomizationStep.REVIEW &&
      !!customizedCourseId &&
      !!currentAccount?.id,
  });

  // Get the language for saving changes
  // Use selectedLanguage if available, otherwise fall back to the customized course's default language
  const saveLanguage =
    selectedLanguage ||
    courseChanges?.customizedCourse?.defaultLanguage ||
    LanguagesEnum.EN;

  // Save changes hook
  const { saveChanges } = useSaveCourseChanges(
    customizedCourseId || undefined,
    courseChanges,
    refetchCourseChanges,
    saveLanguage,
  );

  const [isSaving, setIsSaving] = useState(false);

  const { course: customizedCourse } = useCourse({
    courseId: customizedCourseId || undefined,
    enabled:
      [
        CustomizationStep.PREVIEW,
        CustomizationStep.TRANSLATION,
        CustomizationStep.COMPLETE,
      ].includes(currentStep) && !!customizedCourseId,
    staleTime: Infinity,
  });

  const handleCustomize = async () => {
    if (!currentAccount?.id || !course) {
      return;
    }

    customizeCourse({
      courseId: course.id,
      accountId: currentAccount.id,
      language: selectedLanguage,
    });
  };

  const navigateToCourseContent = () => {
    navigate(
      RedirectPaths[RedirectPathsEnum.COURSES_CONTENT](
        (customizedCourseId ? customizedCourseId : courseId) || '',
      ),
    );
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    try {
      await saveChanges(pendingSlideEdits, pendingQuestionEdits);
      clearPendingEdits();
    } finally {
      setIsSaving(false);
    }
  };

  const handleContinueToPreview = () => {
    if (customizedCourseId) {
      setCurrentStep(CustomizationStep.PREVIEW);
    }
  };

  const handleTranslate = () => {
    setCurrentStep(CustomizationStep.TRANSLATION);
  };

  const handleGoToTrainingPlan = () => {
    navigate(RedirectPaths[RedirectPathsEnum.ACCOUNT_COURSE_PLANNER]());
  };

  const showCompleteStep = () => {
    if (customizedCourseId) {
      setCurrentStep(CustomizationStep.COMPLETE);
    }
  };

  // Determine step states based on current step
  const getStepStates = (): StepState[] => {
    const effectiveStep = isLoading
      ? CustomizationStep.PROCESSING
      : currentStep;
    const currentStepIndex = STEP_ORDER.findIndex(
      (step) => step === effectiveStep,
    );
    const isProcessingComplete = !!customizedCourseId;
    const clickableSteps = [
      CustomizationStep.REVIEW,
      CustomizationStep.PREVIEW,
      CustomizationStep.TRANSLATION,
    ];

    return STEP_ORDER.map((step, index) => {
      const config = STEP_CONFIG[step];
      const isCompleted = index < currentStepIndex;
      const isActive = index === currentStepIndex;
      // Review, Preview, and Translation are clickable after processing is complete
      const isClickable = isProcessingComplete && clickableSteps.includes(step);

      return {
        number: config.number,
        label: t(config.label),
        active: isActive,
        completed: isCompleted,
        clickable: isClickable,
        step: step,
      };
    });
  };

  const handleStepClick = (step: string) => {
    const clickedStep = step as CustomizationStep;
    const clickableSteps = [
      CustomizationStep.REVIEW,
      CustomizationStep.PREVIEW,
      CustomizationStep.TRANSLATION,
    ];
    if (customizedCourseId && clickableSteps.includes(clickedStep)) {
      setCurrentStep(clickedStep);
    }
  };

  // Render step content based on current step
  const renderStepContent = (): React.ReactNode => {
    const effectiveStep = isLoading
      ? CustomizationStep.PROCESSING
      : currentStep;

    switch (effectiveStep) {
      case CustomizationStep.COMPLETE:
        return (
          <CustomizeCourseCompleteStep
            accountId={currentAccount?.id || ''}
            onClose={navigateToCourseContent}
            originalCourse={course}
            customizedCourse={customizedCourse}
            onGoToTrainingPlan={handleGoToTrainingPlan}
          />
        );

      case CustomizationStep.TRANSLATION:
        if (!customizedCourse) return null;
        return (
          <CourseTranslation
            course={customizedCourse}
            onCancel={showCompleteStep}
            onComplete={showCompleteStep}
          />
        );

      case CustomizationStep.PREVIEW:
        if (!customizedCourse) return null;
        return (
          <CustomizeCoursePreviewStep
            course={customizedCourse}
            onSkipTranslation={showCompleteStep}
            onTranslate={handleTranslate}
          />
        );

      case CustomizationStep.REVIEW:
        return (
          <CustomizeCourseReviewStep
            courseChanges={courseChanges}
            isLoadingChanges={isLoadingChanges}
            pendingSlideEdits={pendingSlideEdits}
            pendingQuestionEdits={pendingQuestionEdits}
            onSlideEdit={handleSlideEdit}
            onQuestionEdit={handleQuestionEdit}
            onCancel={navigateToCourseContent}
            onContinue={handleContinueToPreview}
            onSave={handleSaveChanges}
            isSaving={isSaving}
          />
        );

      case CustomizationStep.PROCESSING:
        return (
          <CourseProcessing
            title={t('course.customization.processing.title')}
            description={t('course.customization.processing.description')}
          />
        );

      case CustomizationStep.PREFERENCES:
      default:
        if (!course) return null;
        return (
          <>
            <CustomizeCoursePreferencesStep
              course={course}
              selectedLanguage={selectedLanguage}
              onLanguageChange={setSelectedLanguage}
              isDisabled={isLoading}
            />
            <FlexContainer
              gap="var(--semi-medium-padding)"
              align="center"
              justify="center"
              width="100%"
              className="pt-6"
            >
              <AppButton
                label={t('button.cancel')}
                onClick={navigateToCourseContent}
                isDisabled={isLoading}
                type="outlined"
              />
              <AppButton
                onClick={handleCustomize}
                isDisabled={isLoading || !selectedLanguage}
              >
                {t('course.customization.submit')}
              </AppButton>
            </FlexContainer>
            <FeatureRoadmapSection />
          </>
        );
    }
  };

  const currentSteps = getStepStates();

  // Loading state while fetching course
  if (isCourseLoading || !course) {
    return (
      <FlexContainer direction="column" className="mt-5">
        <ProgressSpinner />
        <h3>{t('content.loading')}</h3>
      </FlexContainer>
    );
  }

  return (
    <FlexContainer
      direction="column"
      align="flex-start"
      justify="flex-start"
      className="h-full"
    >
      <AppBreadCrumb
        model={getBreadcrumbs(
          currentAccount as Account,
          course,
          currentUser as User,
          t,
          courseChanges?.customizedCourse,
        )}
      />

      <FlexContainer
        gap="var(--small-padding)"
        justify="flex-start"
        align="center"
      >
        <h2 className="m-0">
          {getCourseName(
            currentUser as User,
            course,
            courseChanges?.customizedCourse,
          )}
        </h2>
      </FlexContainer>

      <div
        style={{
          background: 'var(--beige-main)',
          borderRadius: 'var(--semi-medium-border-radius)',
          flex: 1,
          width: '100%',
        }}
        className="mt-4 p-5"
      >
        <div>
          {currentStep !== CustomizationStep.COMPLETE && (
            <>
              <CustomizeCourseStepsIndicator
                steps={currentSteps}
                onStepClick={handleStepClick}
              />
              <Divider />
            </>
          )}

          {renderStepContent()}
        </div>
      </div>
    </FlexContainer>
  );
};
